import { HttpClient, HttpHeaders } from '@angular/common/http'
import { computed, Inject, inject, Injectable, signal } from '@angular/core'
import { Router } from '@angular/router'
import { lastValueFrom, Observable, Subject, throwError } from 'rxjs'
import { filter, take, takeUntil } from 'rxjs/operators'
import {
   MsalService,
   MsalBroadcastService,
   MsalGuardConfiguration,
   MSAL_GUARD_CONFIG,
} from '@azure/msal-angular'
import {
   EventMessage,
   AuthenticationResult,
   InteractionStatus,
   EventType,
   RedirectRequest,
   InteractionType,
   AuthError,
   AccountInfo,
   IdTokenClaims,
   EndSessionRequest
} from '@azure/msal-browser'
import { CookieService } from 'ngx-cookie-service'
import { AppSignals, CommonService } from '@appShared/services'
import { B2CAuthority_, b2cPolicies, defaultB2CAuthority } from '@appShared/b2c-policies'
import { SITE_DIRECTORY_ROUTES } from '@appShared/site-directory.routes'
import { environment } from '@appEnvironments/environment'
import * as _ from 'lodash'

export interface loginRequest {
   redirectUrl?: string
   b2cAuthorityType?: B2CAuthority_
}

export class Claim {
   id: number
   claim: string
   value: string
}

@Injectable({ providedIn: 'root' })
export class AuthService {
   /**
    * see all the msal documentation:
    * https://github.com/AzureAD/microsoft-authentication-library-for-js/tree/dev/samples/msal-angular-samples/angular-standalone-sample
    * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/redirects.md#redirects-with-standalone-components
    * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/events.md
    */
   private _msalAuthService = inject(MsalService)
   private _msalBroadcastService = inject(MsalBroadcastService)
   private _cookieService = inject(CookieService)
   private _router = inject(Router)
   private _commonService = inject(CommonService)
   private _appSignals = inject(AppSignals)
   private _delegatedAuthorizationUri = computed(() => this._appSignals.siteConfig().delegatedAuthorizationUri)
   private _msalIsLoggedIn = this._appSignals.msalIsLoggedIn
   private _httpClient = inject(HttpClient)
   private _authApi = '/api/auth'
   private _isDevTest = !environment.production

   redirectUrl = signal<string | undefined>(undefined)

   private readonly _destroying$ = new Subject<void>()
   loginDisplay = false
   claimSource = signal<Claim[]>([])

   constructor(
      @Inject(MSAL_GUARD_CONFIG) private _msalGuardConfig: MsalGuardConfiguration,
      router: Router
   ) {
      this._msalAuthService.initialize().subscribe(() => {

         this._msalAuthService.handleRedirectObservable().pipe(
            takeUntil(this._destroying$)
         ).subscribe({
            next: (result) => {
               if (result) {
                  if (this._isDevTest) {
                     console.log('in handleRedirectObservable', result, (new Date()).getTime())
                     console.log('in handleRedirectObservable:isLoggedIn', this._msalIsLoggedIn(), (new Date()).getTime())
                  }

                  const redirectUrl = result.state
                  router.navigateByUrl(redirectUrl)
               }
            },
            error: (error) => console.error(error)
         })

         // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
         this._msalAuthService.instance.enableAccountStorageEvents()
         this._msalBroadcastService.msalSubject$
            .pipe(
               filter(
                  (msg: EventMessage) =>
                     msg.eventType === EventType.ACCOUNT_ADDED ||
                     msg.eventType === EventType.ACCOUNT_REMOVED
               ),
               takeUntil(this._destroying$)
            )
            .subscribe((result: EventMessage) => {
               if (this._isDevTest) {
                  console.log('msalSubject$: ACCOUNT_ADDED|ACCOUNT_REMOVED:', result, (new Date()).getTime())
               }

               if (this._msalAuthService.instance.getAllAccounts().length === 0) {
                  window.location.pathname = '/'
               }
            })

         this._msalBroadcastService.msalSubject$
            .pipe(
               filter(
                  (msg: EventMessage) =>
                     msg.eventType === EventType.LOGIN_START ||
                     msg.eventType === EventType.ACQUIRE_TOKEN_START ||
                     msg.eventType === EventType.SSO_SILENT_START
               ),
               takeUntil(this._destroying$)
            )
            .subscribe((result: EventMessage) => {
               if (this._isDevTest) {
                  console.log('msalSubject$: LOGIN_START|ACQUIRE_TOKEN_START|SSO_SILENT_START', result, (new Date()).getTime())
               }
            })

         this._msalBroadcastService.msalSubject$
            .pipe(
               filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
                  || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
                  || msg.eventType === EventType.SSO_SILENT_SUCCESS),
               takeUntil(this._destroying$)
            )
            .subscribe((result: EventMessage) => {
               let payload = result.payload as AuthenticationResult
               let idtoken = payload.idTokenClaims as IdTokenClaims

               if (this._isDevTest) {
                  console.log('LOGGED msalSubject$: LOGIN_SUCCESS|ACQUIRE_TOKEN_SUCCESS|SSO_SILENT_SUCCESS:', result, (new Date()).getTime())
                  console.log('LOGGED msalSubject$: LOGIN_SUCCESS|ACQUIRE_TOKEN_SUCCESS|SSO_SILENT_SUCCESS: idtoken', idtoken, (new Date()).getTime())
               }

               const signInNames = [
                  b2cPolicies.names.defaultSignIn] as any[]

               if (signInNames.includes(idtoken.acr) || signInNames.includes(idtoken.tfp)) {
                  let activeAccount = payload?.account
                  this._msalAuthService.instance.setActiveAccount(activeAccount)
                  this._getClaims(activeAccount?.idTokenClaims)
               }

               /*
                  !!! MSAL is writing "session" encryption cookie (which works in conjuntion with localStorage),
                  so if all instances of browser are closed, returning to the site requires user to login again
                   - to remember, we are overwriting their cookie to expire in future (90 days)
               */
               const msalCacheEncryptionKey = 'msal.cache.encryption'
               let msalCookie = this._cookieService.get(msalCacheEncryptionKey)
               this._cookieService.set(msalCacheEncryptionKey, msalCookie, 90, '/', undefined, true, 'None')


               /**
                * For the purpose of setting an active account for UI update, we want to consider only the auth response resulting
                * from SUSI flow. "acr" claim in the id token tells us the policy (NOTE: newer policies may use the "tfp" claim instead).
                * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
                */
               //TODO - maybe
               //if (idtoken.acr === environment.b2cPolicies.names.editProfile || idtoken.tfp === environment.b2cPolicies.names.editProfile) {

               //   // retrieve the account from initial sing-in to the app
               //   const originalSignInAccount = this._msalAuthService.instance.getAllAccounts()
               //      .find((account: AccountInfo) =>
               //         account.idTokenClaims?.oid === idtoken.oid
               //         && account.idTokenClaims?.sub === idtoken.sub
               //         && ((account.idTokenClaims as IdTokenClaims).acr === environment.b2cPolicies.names.signUpSignIn
               //            || (account.idTokenClaims as IdTokenClaimsWithPolicyId).tfp === environment.b2cPolicies.names.signUpSignIn)
               //      )

               //   let signUpSignInFlowRequest: PopupRequest = {
               //      scopes: [...environment.apiConfig.scopes],
               //      authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
               //      account: originalSignInAccount,
               //      prompt: PromptValue.NONE
               //   }

               //   // To get the updated account information
               //   this._msalAuthService.acquireTokenPopup(signUpSignInFlowRequest)
               //}

               /**
                * Below we are checking if the user is returning from the reset password flow.
                * If so, we will ask the user to reauthenticate with their new password.
                * If you do not want this behavior and prefer your users to stay signed in instead,
                * you can replace the code below with the same pattern used for handling the return from
                * profile edit flow (see above ln. 74-92).
                */
               //TODO - maybe
               //if (idtoken.acr === environment.b2cPolicies.names.resetPassword || idtoken.tfp === environment.b2cPolicies.names.resetPassword) {
               //   let signUpSignInFlowRequest: RedirectRequest | PopupRequest = {
               //      authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
               //      scopes: [...environment.apiConfig.scopes],
               //      prompt: PromptValue.LOGIN // force user to reauthenticate with their new password
               //   }

               //   this.login(signUpSignInFlowRequest)
               //}

               return result
            })

         this._msalBroadcastService.msalSubject$
            .pipe(
               // Optional filtering of events
               filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS),
               takeUntil(this._destroying$)
            )
            .subscribe((result: EventMessage) => {
               this._appSignals.setMsalIsLoggedIn(false)
            })

         this._msalBroadcastService.msalSubject$
            .pipe(
               // Optional filtering of events
               filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE
                  || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
                  || msg.eventType === EventType.SSO_SILENT_FAILURE),
               takeUntil(this._destroying$)
            )
            .subscribe((result: EventMessage) => {
               if (this._isDevTest) {
                  console.log('LOGGED msalSubject$: LOGIN_FAILURE|ACQUIRE_TOKEN_FAILURE result:', result, (new Date()).getTime())
               }
               if (result.error instanceof AuthError) {
                  console.log('LOGGED msalSubject$: LOGIN_FAILURE|ACQUIRE_TOKEN_FAILURE error:', result.error, (new Date()).getTime())
               }
            })

         this._msalBroadcastService.inProgress$
            .pipe(
               filter(
                  (status: InteractionStatus) => status === InteractionStatus.None
               ),
               takeUntil(this._destroying$)
            )
            .subscribe(() => {
               if (this._isDevTest) {
                  console.log('inProgress$: InteractionStatus.None', (new Date()).getTime())
               }

               let activeAccount = this._msalAuthService.instance.getActiveAccount()
               this.checkAndSetActiveAccount(activeAccount)
            })
      })
   }

   ngOnDestroy(): void {
      this._destroying$.next(undefined)
      this._destroying$.complete()
   }

   /*
    public methods
   */

   checkAndSetActiveAccount(activeAccount?: AccountInfo) {
      /**
       * If no active account set but there are accounts signed in, sets first account to active account
       * To use active account set here, subscribe to inProgress$ first in your component
       * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
       */

      const accounts = this._msalAuthService.instance.getAllAccounts()
      const hasAccounts = accounts.length > 0

      if (!activeAccount && hasAccounts) {
         activeAccount = accounts[0]
         this._msalAuthService.instance.setActiveAccount(activeAccount)
      }

      this._appSignals.setMsalIsLoggedIn(hasAccounts)

      if (this._isDevTest) {
         console.log('LOGGED IN:checkAndSetActiveAccount:hasAccounts|activeAccount', hasAccounts, activeAccount, (new Date()).getTime())
      }

      this._getClaims(activeAccount?.idTokenClaims)
   }

   private _configureRedirectRequest(request?: RedirectRequest): RedirectRequest {

      const authority = request.authority || defaultB2CAuthority.authority
      const state = request.state || `/${SITE_DIRECTORY_ROUTES.content.uri}`

      let redirectRequest = {} as RedirectRequest

      if (this._msalGuardConfig.authRequest) {
         redirectRequest = {
            ...this._msalGuardConfig.authRequest
         } as RedirectRequest
      }

      redirectRequest = {
         ...redirectRequest,
         authority,
         state
      } as RedirectRequest

      return redirectRequest
   }

   login(request?: RedirectRequest) {
      const redirectRequest = this._configureRedirectRequest(request)

      if (this._msalGuardConfig.interactionType === InteractionType.Redirect) {

         this._msalAuthService.instance.loginRedirect(redirectRequest)

      } else {

         this._msalAuthService.loginPopup(redirectRequest)
            .subscribe((response: AuthenticationResult) => {
               this._msalAuthService.instance.setActiveAccount(response.account)

               let returnUrl = redirectRequest.state || '/'
               this._router.navigateByUrl(returnUrl)
            })
      }
   }

   logout(redirectUrl?: string, popup?: boolean) {
      if (popup) {
         this._msalAuthService.logoutPopup({
            mainWindowRedirectUri: '/',
         })
      } else {
         if (redirectUrl) {
            const logoutRequest = {
               account: this._msalAuthService.instance.getActiveAccount(),
               postLogoutRedirectUri: redirectUrl
            } as EndSessionRequest
            this._msalAuthService.logoutRedirect(logoutRequest)
         }

         this._msalAuthService.logoutRedirect()
      }
   }

   logoutRegistrationRedirect(token?: string) {
      if (!token) {
         this.logout()
      }

      const registrationUri = `/auth#token=${encodeURIComponent(token)}`
      this.logout(registrationUri)
   }

   //TODO maybe
   //editProfile() {
   //   let editProfileFlowRequest: RedirectRequest | PopupRequest = {
   //      authority: environment.b2cPolicies.authorities.editProfile.authority,
   //      scopes: [],
   //   }

   //   this.login(editProfileFlowRequest)
   //}

   private _getClaims(claims: any) {

      let list: Claim[] = new Array<Claim>()

      if (claims) {
         Object.keys(claims).forEach(function (k, v) {

            let c = new Claim()
            c.id = v
            c.claim = k
            c.value = claims ? claims[k] : null
            list.push(c)
         })
      }

      if (this._isDevTest) {
         console.log('Token claims', list)
      }

      this.claimSource.set(list)

   }
}
